import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import {
  clearActiveGameSessionSnapshot,
  isValidActiveGameSessionSnapshot,
  writeActiveGameSessionSnapshot,
} from '../services/activeGameSessionStorage';
import { conversationLogger } from '../services/conversationLogger';
import { generateRequestId, reportClientError } from '../services/errorReporter';
import { UsageTracker } from '../services/usageTracker';

const PLAYED_QUEST_PACKS_KEY = 'cq_played_quest_packs';
const PENDING_CHECKOUT_SELECTION_KEY = 'cq_pending_checkout_selection';
const formatStoreCatalog = (items = [], ownedGamePackIds = [], ownedQuestPackIds = []) => {
  const ownedGameSet = new Set(ownedGamePackIds || []);
  const ownedQuestSet = new Set(ownedQuestPackIds || []);
  const storeItemByGamePackId = new Map();
  const storeItemByQuestPackId = new Map();

  (items || []).forEach((item) => {
    if (item?.item_type === 'game_pack' && item.related_game_pack_id) {
      storeItemByGamePackId.set(item.related_game_pack_id, item);
    }

    if (item?.item_type === 'quest_pack' && item.related_quest_pack_id) {
      storeItemByQuestPackId.set(item.related_quest_pack_id, item);
    }
  });

  return {
    ownedGameSet,
    ownedQuestSet,
    storeItemByGamePackId,
    storeItemByQuestPackId,
  };
};

/**
 * useGameState Hook
 * 
 * Manages core game state including conversation flow, players, questions,
 * and quest pack selection. Extracted from ChatApp component.
 */
export const useGameState = ({ 
  authenticatedUser, 
  clientInfo, 
  onUsageUpdate,
  initializeWebRTCConnection,
  conversationMode,
  isAdmin = false,
}) => {
  const createEmptyUsageTotals = () => ({
    newInputTokens: 0,
    cachedTokens: 0,
    outputTokens: 0,
    totalCost: 0,
    newInputTextTokens: 0,
    newInputAudioTokens: 0,
    cachedTextTokens: 0,
    cachedAudioTokens: 0,
    outputTextTokens: 0,
    outputAudioTokens: 0,
    usageEventCount: 0,
    usageEvents: [],
  });

  const parseApiResponse = async (response) => {
    const contentType = response.headers?.get?.('content-type') || '';

    if (typeof response.text === 'function') {
      const text = await response.text();
      let data = null;

      try {
        data = text ? JSON.parse(text) : null;
      } catch (error) {
        data = null;
      }

      return {
        contentType,
        data,
        text: text?.trim() || '',
      };
    }

    if (typeof response.json === 'function') {
      const data = await response.json();

      return {
        contentType,
        data,
        text: '',
      };
    }

    return {
      contentType,
      data: null,
      text: '',
    };
  };

  // Game flow state
  const [conversationStarted, setConversationStarted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeSessionRestore, setActiveSessionRestore] = useState({
    status: 'idle',
    error: '',
    reason: '',
  });
  
  // Player and scoring state
  const [players, setPlayersState] = useState({});
  const playersRef = useRef(players);
  
  // Conversation state
  const [conversation, setConversationState] = useState([]);
  const conversationRef = useRef(conversation);
  const [messageCount, setMessageCountState] = useState(1);
  const messageCountRef = useRef(messageCount);
  
  // Game pack state
  const [selectedGamePack, setSelectedGamePackState] = useState('');
  const [gamePacks, setGamePacks] = useState([]);

  // Quest pack state
  const [selectedPack, setSelectedPackState] = useState('');
  const [questPacksByGamePack, setQuestPacksByGamePack] = useState({});
  const [questPackLoadingByGamePack, setQuestPackLoadingByGamePack] = useState({});
  const [questPackErrorsByGamePack, setQuestPackErrorsByGamePack] = useState({});
  const questPackRequestsRef = useRef({});
  const selectedPackRef = useRef(selectedPack);

  // Active tool definitions for the current conversation
  const [activeTools, setActiveTools] = useState([]);

  // Wallet state
  const [wallet, setWallet] = useState({
    creditBalance: 0,
    ownedQuestPackIds: [],
    ownedGamePackIds: [],
    recentTransactions: [],
    storeItems: [],
  });
  const [walletLoaded, setWalletLoaded] = useState(false);
  const [walletError, setWalletError] = useState('');
  const [purchasingItemId, setPurchasingItemId] = useState(null);
  
  // Current question state
  const [currentQuestion, setCurrentQuestion] = useState({ question: '', round: null });
  const [finalResults, setFinalResults] = useState(null);
  const sessionLimitsRef = useRef(null);
  const [sessionLimits, setSessionLimits] = useState({
    max_input_tokens: 10000,
    max_output_tokens: 10000,
    warning_threshold: 0.8,
    idle_warning_seconds: 120,
    idle_auto_end_seconds: 30,
  });
  
  // Usage tracking
  const [totalUsage, setTotalUsageState] = useState(createEmptyUsageTotals);
  const totalUsageRef = useRef(totalUsage);
  const usageTrackerRef = useRef(null);
  // Track the current paid game session id for finalization/logging linkage
  const gameSessionIdRef = useRef(null);
  const sessionRequestIdRef = useRef(null);
  const sessionStartedAtRef = useRef(null);
  const realtimeModelRef = useRef('gpt-realtime-1.5');
  const realtimeReasoningEffortRef = useRef('low');
  const realtimeConfigRef = useRef({});

  // Update conversation ref when conversation changes
  useEffect(() => {
    conversationRef.current = conversation;
  }, [conversation]);

  const setPlayers = (nextPlayersOrUpdater) => {
    const nextPlayers = typeof nextPlayersOrUpdater === 'function'
      ? nextPlayersOrUpdater(playersRef.current)
      : nextPlayersOrUpdater;

    playersRef.current = nextPlayers;
    setPlayersState(nextPlayers);
  };

  const setConversation = (nextConversationOrUpdater) => {
    const nextConversation = typeof nextConversationOrUpdater === 'function'
      ? nextConversationOrUpdater(conversationRef.current)
      : nextConversationOrUpdater;

    conversationRef.current = nextConversation;
    setConversationState(nextConversation);
  };

  const appendConversationMessage = (message) => {
    if (!message?.role || typeof message.content !== 'string') {
      return;
    }

    setConversation((current) => [
      ...current,
      {
        timestamp: new Date().toISOString(),
        ...message,
      },
    ]);
  };

  const handleRealtimeConversationEvent = (responseData) => {
    const isTranscriptDone = responseData?.type === "response.audio_transcript.done" ||
      responseData?.type === "response.output_audio_transcript.done";
    const isTextDelta = responseData?.type === "response.text.delta" ||
      responseData?.type === "response.output_text.delta";
    const isTextDone = responseData?.type === "response.text.done" ||
      responseData?.type === "response.output_text.done";

    if (isTranscriptDone && responseData.transcript) {
      appendConversationMessage({
        role: 'assistant',
        content: responseData.transcript,
        channel: 'voice',
      });
      return;
    }

    if (isTextDelta && responseData.text) {
      setConversation((current) => {
        if (
          current.length > 0 &&
          current[current.length - 1].role === 'assistant' &&
          current[current.length - 1].incomplete
        ) {
          const updated = [...current];
          updated[updated.length - 1] = {
            ...updated[updated.length - 1],
            content: `${updated[updated.length - 1].content}${responseData.text}`,
          };
          return updated;
        }

        return [
          ...current,
          {
            role: 'assistant',
            content: responseData.text,
            timestamp: new Date().toISOString(),
            incomplete: true,
          },
        ];
      });
      return;
    }

    if (isTextDone && responseData.text) {
      setConversation((current) => {
        if (
          current.length > 0 &&
          current[current.length - 1].role === 'assistant' &&
          current[current.length - 1].incomplete
        ) {
          const updated = [...current];
          updated[updated.length - 1] = {
            ...updated[updated.length - 1],
            content: `${updated[updated.length - 1].content}${responseData.text}`,
            incomplete: false,
          };
          return updated;
        }

        return [
          ...current,
          {
            role: 'assistant',
            content: responseData.text,
            timestamp: new Date().toISOString(),
          },
        ];
      });
    }
  };

  const setMessageCount = (nextMessageCountOrUpdater) => {
    const nextMessageCount = typeof nextMessageCountOrUpdater === 'function'
      ? nextMessageCountOrUpdater(messageCountRef.current)
      : nextMessageCountOrUpdater;

    messageCountRef.current = nextMessageCount;
    setMessageCountState(nextMessageCount);
  };

  const setSelectedPack = (nextSelectedPackOrUpdater) => {
    const nextSelectedPack = typeof nextSelectedPackOrUpdater === 'function'
      ? nextSelectedPackOrUpdater(selectedPackRef.current)
      : nextSelectedPackOrUpdater;

    selectedPackRef.current = nextSelectedPack;
    setSelectedPackState(nextSelectedPack);
  };

  const setTotalUsage = (nextTotalUsageOrUpdater) => {
    const nextTotalUsage = typeof nextTotalUsageOrUpdater === 'function'
      ? nextTotalUsageOrUpdater(totalUsageRef.current)
      : nextTotalUsageOrUpdater;

    totalUsageRef.current = nextTotalUsage;
    setTotalUsageState(nextTotalUsage);
  };

  // Helper function to get user ID from authenticatedUser object
  const getUserId = () => {
    let userId = authenticatedUser?.user?.id || authenticatedUser?.user?.user_id || authenticatedUser?.id;

    if (!userId && authenticatedUser?.token) {
      try {
        const tokenPayload = JSON.parse(atob(authenticatedUser.token.split('.')[1]));
        userId = tokenPayload.sub || tokenPayload.user_id || tokenPayload.id;
      } catch (error) {
        console.warn("Could not extract user ID from JWT:", error);
      }
    }

    return userId;
  };

  // Create initial conversation with system prompt
  const createInitialConversation = (prompt) => [
    { role: "system", content: prompt, timestamp: new Date().toISOString() }
  ];

  const fetchWallet = useCallback(async () => {
    const token = localStorage.getItem('auth_token');

    if (!token || !authenticatedUser) {
      setWallet({
        creditBalance: 0,
        ownedQuestPackIds: [],
        ownedGamePackIds: [],
        recentTransactions: [],
        storeItems: [],
      });
      setWalletLoaded(false);
      return null;
    }

    try {
      setWalletError('');
      setWalletLoaded(false);

      const response = await fetch('/.netlify/functions/get-wallet', {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (!response.ok || data?.success === false) {
        throw new Error(data?.message || data?.error || 'Failed to load wallet.');
      }

      setWallet({
        creditBalance: data.creditBalance ?? 0,
        ownedQuestPackIds: data.ownedQuestPackIds || [],
        ownedGamePackIds: data.ownedGamePackIds || [],
        recentTransactions: data.recentTransactions || [],
        storeItems: data.storeItems || [],
      });
      setWalletLoaded(true);
      return data;
    } catch (error) {
      console.error('Error fetching wallet:', error);
      setWalletError('Failed to load wallet.');
      return null;
    }
  }, [authenticatedUser]);

  const purchaseStoreItem = async (purchasableItemId, options = {}) => {
    const token = localStorage.getItem('auth_token');
    const itemLabel = options.itemLabel || 'this item';

    if (!token) {
      toast.error(`You must be signed in to buy ${itemLabel}.`);
      return;
    }

    try {
      setPurchasingItemId(purchasableItemId);

      const response = await fetch('/.netlify/functions/create-checkout-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ purchasableItemId }),
      });

      const data = await response.json();

      if (!response.ok || data?.success === false || !data?.checkoutUrl) {
        throw new Error(data?.message || data?.error || 'Failed to start checkout.');
      }

      if (options.pendingSelection && typeof window !== 'undefined') {
        window.localStorage.setItem(
          PENDING_CHECKOUT_SELECTION_KEY,
          JSON.stringify(options.pendingSelection)
        );
      }

      window.location.assign(data.checkoutUrl);
    } catch (error) {
      console.error('Error creating checkout session:', error);
      toast.error(error.message || 'Failed to start checkout.');
      setPurchasingItemId(null);
    }
  };

  const purchaseCredits = async (purchasableItemId) => (
    purchaseStoreItem(purchasableItemId, { itemLabel: 'credits' })
  );

  const rememberRecentlyPlayedContent = () => {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      if (!selectedGamePackRequiresQuestPack || !selectedPack) {
        return;
      }

      const currentIds = new Set(
        JSON.parse(window.localStorage.getItem(PLAYED_QUEST_PACKS_KEY) || '[]')
      );
      currentIds.add(selectedPack);
      window.localStorage.setItem(PLAYED_QUEST_PACKS_KEY, JSON.stringify([...currentIds]));
    } catch (error) {
      console.warn('Failed to persist recently played content state:', error);
    }
  };

  // Fetch game packs from API
  const fetchGamePacks = useCallback(async () => {
    try {
      const headers = authenticatedUser?.token
        ? { Authorization: `Bearer ${authenticatedUser.token}` }
        : undefined;
      const response = headers
        ? await fetch('/.netlify/functions/fetchGamePacks', { headers })
        : await fetch('/.netlify/functions/fetchGamePacks');
      if (!response.ok) {
        throw new Error('Failed to fetch Games.');
      }
      const data = await response.json();
      setGamePacks(data);
    } catch (err) {
      console.error('Error fetching game packs:', err);
      toast.error('Failed to load Games. Please try again.');
    }
  }, [authenticatedUser?.token]);

  // Fetch quest packs from API and cache them by game pack id.
  const fetchQuestPacks = async (gamePackId) => {
    if (!gamePackId) {
      return [];
    }

    if (questPacksByGamePack[gamePackId]) {
      return questPacksByGamePack[gamePackId];
    }

    if (questPackRequestsRef.current[gamePackId]) {
      return questPackRequestsRef.current[gamePackId];
    }

    const request = (async () => {
      setQuestPackLoadingByGamePack((current) => ({
        ...current,
        [gamePackId]: true,
      }));
      setQuestPackErrorsByGamePack((current) => ({
        ...current,
        [gamePackId]: '',
      }));

      try {
        const response = await fetch(`/.netlify/functions/fetchQuestPacks?gamePackId=${encodeURIComponent(gamePackId)}`);
        if (!response.ok) {
          throw new Error('Failed to fetch Quest Packs.');
        }

        const data = await response.json();

        setQuestPacksByGamePack((current) => ({
          ...current,
          [gamePackId]: data,
        }));
        setQuestPackErrorsByGamePack((current) => ({
          ...current,
          [gamePackId]: data.length === 0 ? 'No Quest Packs available for this Game.' : '',
        }));

        return data;
      } catch (err) {
        console.error('Error fetching quest packs:', err);
        setQuestPacksByGamePack((current) => ({
          ...current,
          [gamePackId]: [],
        }));
        setQuestPackErrorsByGamePack((current) => ({
          ...current,
          [gamePackId]: 'Failed to load Quest Packs. Please try again.',
        }));
        toast.error('Failed to load Quest Packs. Please try again.');
        return [];
      } finally {
        setQuestPackLoadingByGamePack((current) => ({
          ...current,
          [gamePackId]: false,
        }));
        delete questPackRequestsRef.current[gamePackId];
      }
    })();

    questPackRequestsRef.current[gamePackId] = request;
    return request;
  };

  // Initialize pack options on mount
  useEffect(() => {
    fetchGamePacks();
  }, [fetchGamePacks]);

  useEffect(() => {
    fetchWallet();
  }, [fetchWallet]);

  useEffect(() => {
    const availableGamePacks = gamePacks.filter((pack) => isAdmin || !pack.admin_only);

    if (selectedGamePack && !availableGamePacks.some((pack) => pack.id === selectedGamePack)) {
      setSelectedGamePackState('');
      setSelectedPack('');
    }
  }, [gamePacks, isAdmin, selectedGamePack]);

  const { ownedGameSet, ownedQuestSet, storeItemByGamePackId, storeItemByQuestPackId } = useMemo(
    () => formatStoreCatalog(
      wallet.storeItems,
      wallet.ownedGamePackIds,
      wallet.ownedQuestPackIds,
    ),
    [wallet.ownedGamePackIds, wallet.ownedQuestPackIds, wallet.storeItems]
  );

  const enrichedGamePacks = useMemo(
    () => gamePacks.map((pack) => {
      const storeItem = storeItemByGamePackId.get(pack.id) || null;
      const isOwned = Boolean(!pack.is_premium || ownedGameSet.has(pack.id));

      return {
        ...pack,
        is_owned: isOwned,
        is_locked: Boolean(pack.is_premium && !isOwned),
        purchasable_item_id: storeItem?.id || null,
        price_usd: storeItem?.price_usd ?? null,
        unlock_description: storeItem?.description || pack.description || '',
      };
    }),
    [gamePacks, ownedGameSet, storeItemByGamePackId]
  );

  const selectedGamePackDetails = enrichedGamePacks.find((pack) => pack.id === selectedGamePack) || null;
  const selectedGamePackRequiresQuestPack = Boolean(selectedGamePackDetails?.include_questions);
  const selectedGamePackCost = Number(selectedGamePackDetails?.session_credit_cost || 0);
  const questPackQuestions = useMemo(
    () => (
      selectedGamePack
        ? (questPacksByGamePack[selectedGamePack] || []).map((pack) => {
            const storeItem = storeItemByQuestPackId.get(pack.id) || null;
            const isOwned = Boolean(!pack.is_premium || ownedQuestSet.has(pack.id));

            return {
              ...pack,
              is_owned: isOwned,
              is_locked: Boolean(pack.is_premium && !isOwned),
              purchasable_item_id: storeItem?.id || null,
              price_usd: storeItem?.price_usd ?? null,
              unlock_description: storeItem?.description || pack.description || '',
            };
          })
        : []
    ),
    [ownedQuestSet, questPacksByGamePack, selectedGamePack, storeItemByQuestPackId]
  );
  const questPackLoading = Boolean(selectedGamePack && questPackLoadingByGamePack[selectedGamePack]);
  const questPackError = selectedGamePack ? (questPackErrorsByGamePack[selectedGamePack] || '') : '';
  const readyToStart = Boolean(
    selectedGamePack &&
    (
      !selectedGamePackRequiresQuestPack ||
      (selectedPack && questPackQuestions.some((pack) => pack.id === selectedPack))
    )
  );
  const projectedRemainingCredits = wallet.creditBalance - selectedGamePackCost;

  const persistActiveSessionSnapshot = useCallback((overrides = {}) => {
    if (!gameSessionIdRef.current || !selectedGamePack) {
      return null;
    }

    const now = new Date().toISOString();
    return writeActiveGameSessionSnapshot({
      gameSessionId: gameSessionIdRef.current,
      requestId: sessionRequestIdRef.current,
      status: conversationStarted ? 'active' : 'starting',
      selectedGamePack,
      selectedQuestPack: selectedGamePackRequiresQuestPack ? selectedPackRef.current : null,
      conversationMode,
      currentQuestion,
      players: playersRef.current,
      messageCount: messageCountRef.current,
      aiReplyCount: 0,
      totalUsage: totalUsageRef.current,
      sessionLimits: sessionLimitsRef.current || sessionLimits,
      activeTools,
      realtimeConfig: realtimeConfigRef.current,
      conversationSummary: '',
      transcript: conversationRef.current
        .filter((message) => message?.role !== 'system')
        .slice(-20),
      startedAt: sessionStartedAtRef.current || now,
      lastActivityAt: now,
      ...overrides,
    });
  }, [
    activeTools,
    conversationMode,
    conversationStarted,
    currentQuestion,
    selectedGamePack,
    selectedGamePackRequiresQuestPack,
    sessionLimits,
  ]);

  const sendActiveGameSessionHeartbeat = useCallback(async (status = 'active', options = {}) => {
    if (!gameSessionIdRef.current) {
      return false;
    }

    const normalizedStatus = ['active', 'hidden', 'reconnecting'].includes(status)
      ? status
      : 'active';
    const aiReplyCount = Number.isFinite(Number(options.aiReplyCount))
      ? Number(options.aiReplyCount)
      : 0;

    let token = null;
    try {
      token = localStorage.getItem('auth_token');
    } catch {
      token = null;
    }

    const payload = {
      gameSessionId: gameSessionIdRef.current,
      status: normalizedStatus,
      messageCount: messageCountRef.current || 0,
      aiReplyCount,
      lastActivityAt: new Date().toISOString(),
      requestId: sessionRequestIdRef.current,
      token,
    };

    const body = JSON.stringify(payload);

    if (options.useBeacon && typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      const sent = navigator.sendBeacon('/.netlify/functions/heartbeat-game-session', body);
      if (sent) {
        return true;
      }
    }

    const response = await fetch('/.netlify/functions/heartbeat-game-session', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body,
      keepalive: Boolean(options.keepalive),
    });

    return response.ok;
  }, []);

  useEffect(() => {
    if (!conversationStarted) {
      return;
    }

    persistActiveSessionSnapshot();
  }, [
    activeTools,
    conversationStarted,
    currentQuestion,
    messageCount,
    persistActiveSessionSnapshot,
    players,
    selectedPack,
    sessionLimits,
    totalUsage,
  ]);

  useEffect(() => {
    if (!conversationStarted) {
      return undefined;
    }

    const persistBeforeSuspension = () => {
      persistActiveSessionSnapshot();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        persistBeforeSuspension();
      }
    };

    window.addEventListener('pagehide', persistBeforeSuspension);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('pagehide', persistBeforeSuspension);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [conversationStarted, persistActiveSessionSnapshot]);

  useEffect(() => {
    if (!selectedGamePackRequiresQuestPack) {
      if (selectedPack) {
        setSelectedPack('');
      }
      return;
    }

    if (selectedPack && !questPackQuestions.some((pack) => pack.id === selectedPack)) {
      setSelectedPack('');
    }
  }, [questPackQuestions, selectedPack, selectedGamePackRequiresQuestPack]);

  const setSelectedGamePack = async (gamePackId) => {
    if (!gamePackId || gamePackId === selectedGamePack) {
      if (gamePackId && selectedGamePackRequiresQuestPack) {
        await fetchQuestPacks(gamePackId);
      }
      return;
    }

    const nextPack = enrichedGamePacks.find((pack) => pack.id === gamePackId) || null;

    if (nextPack?.is_locked) {
      toast.error(nextPack.name ? `${nextPack.name} is locked for this account.` : 'This game pack is locked.');
      return;
    }

    setSelectedGamePackState(gamePackId);
    setSelectedPack('');

    if (nextPack?.include_questions) {
      await fetchQuestPacks(gamePackId);
      return;
    }

    setQuestPackErrorsByGamePack((current) => ({
      ...current,
      [gamePackId]: '',
    }));
  };

  const clearSelectedGamePack = () => {
    setSelectedGamePackState('');
    setSelectedPack('');
  };

  const hydrateActiveSessionSnapshot = (snapshot) => {
    if (!isValidActiveGameSessionSnapshot(snapshot)) {
      return false;
    }

    gameSessionIdRef.current = snapshot.gameSessionId;
    sessionRequestIdRef.current = snapshot.requestId || generateRequestId();
    sessionStartedAtRef.current = snapshot.startedAt || new Date().toISOString();
    realtimeConfigRef.current = snapshot.realtimeConfig || {};
    sessionLimitsRef.current = snapshot.sessionLimits || sessionLimitsRef.current;

    setSelectedGamePackState(snapshot.selectedGamePack);
    setSelectedPack(snapshot.selectedQuestPack || '');
    setCurrentQuestion(snapshot.currentQuestion || { question: '', round: null });
    setPlayers(snapshot.players || {});
    setMessageCount(Number(snapshot.messageCount) || 1);
    setTotalUsage({
      ...createEmptyUsageTotals(),
      ...(snapshot.totalUsage || {}),
    });
    setSessionLimits(sessionLimitsRef.current || sessionLimits);
    setActiveTools(Array.isArray(snapshot.activeTools) ? snapshot.activeTools : []);

    const restoredTranscript = Array.isArray(snapshot.transcript) ? snapshot.transcript : [];
    setConversation(restoredTranscript);

    return true;
  };

  const resumeActiveSessionFromSnapshot = async (snapshot, initializeWebRTCConnectionOverride) => {
    if (!hydrateActiveSessionSnapshot(snapshot)) {
      clearActiveGameSessionSnapshot();
      return false;
    }

    const webRtcInitializer = typeof initializeWebRTCConnectionOverride === 'function'
      ? initializeWebRTCConnectionOverride
      : initializeWebRTCConnection;

    if (typeof webRtcInitializer !== 'function') {
      setActiveSessionRestore({
        status: 'failed',
        error: 'Missing WebRTC initializer.',
        reason: '',
      });
      return false;
    }

    setLoading(true);
    setActiveSessionRestore({ status: 'resuming', error: '', reason: '' });

    try {
      const token = localStorage.getItem('auth_token');
      const requestId = sessionRequestIdRef.current || generateRequestId();
      sessionRequestIdRef.current = requestId;

      const response = await fetch('/.netlify/functions/resume-paid-game', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          'X-Request-Id': requestId,
        },
        body: JSON.stringify({
          gameSessionId: snapshot.gameSessionId,
          requestId,
          conversationMode: snapshot.conversationMode || conversationMode,
          clientSnapshot: {
            currentQuestion: snapshot.currentQuestion || null,
            players: snapshot.players || {},
            messageCount: Number(snapshot.messageCount) || 1,
            aiReplyCount: Number(snapshot.aiReplyCount) || 0,
            conversationSummary: snapshot.conversationSummary || '',
            transcript: Array.isArray(snapshot.transcript) ? snapshot.transcript.slice(-20) : [],
          },
        }),
      });

      const { data, text, contentType } = await parseApiResponse(response);

      if (!response.ok || data?.success === false) {
        const fallbackMessage = text
          ? (contentType.includes('text/html')
            ? `resume-paid-game returned ${response.status} with a non-JSON error response. Check Netlify function logs.`
            : text.slice(0, 400))
          : `Failed to resume game. Status: ${response.status}`;
        throw new Error(data?.message || data?.error || fallbackMessage);
      }

      if (!data?.resumable) {
        clearActiveGameSessionSnapshot();
        setConversationStarted(false);
        setActiveSessionRestore({
          status: 'not_resumable',
          error: '',
          reason: data?.reason || 'not_resumable',
        });
        await fetchWallet();
        return false;
      }

      const result = data.data || data;

      if (!result.realtimeSessionToken || !result.dynamicPrompt) {
        throw new Error('Resume response was missing realtime setup data.');
      }

      const initialConversation = createInitialConversation(result.dynamicPrompt);
      const restoredTranscript = Array.isArray(snapshot.transcript) ? snapshot.transcript.slice(-20) : [];
      const restoredConversation = [...initialConversation, ...restoredTranscript];

      setConversation(restoredConversation);
      setActiveTools(result.tools || []);
      realtimeModelRef.current = result.model_used || snapshot.realtimeConfig?.model || 'gpt-realtime-1.5';
      realtimeReasoningEffortRef.current = result.reasoning_effort || snapshot.realtimeConfig?.reasoningEffort || 'low';
      gameSessionIdRef.current = result.gameSessionId || result.game_session_id || snapshot.gameSessionId;

      if (result.sessionLimits && typeof result.sessionLimits === 'object') {
        sessionLimitsRef.current = {
          max_input_tokens: Number(result.sessionLimits.max_input_tokens) || 10000,
          max_output_tokens: Number(result.sessionLimits.max_output_tokens) || 10000,
          warning_threshold: Number(result.sessionLimits.warning_threshold) || 0.8,
          idle_warning_seconds: Number(result.sessionLimits.idle_warning_seconds) || 120,
          idle_auto_end_seconds: Number(result.sessionLimits.idle_auto_end_seconds) || 30,
        };
        setSessionLimits(sessionLimitsRef.current);
      }

      const realtimeConfig = {
        model: realtimeModelRef.current,
        reasoningEffort: realtimeReasoningEffortRef.current,
        outputModalities: result.output_modalities || snapshot.realtimeConfig?.outputModalities || null,
        voice: result.voice || snapshot.realtimeConfig?.voice || null,
        idleRecovery: result.idle_recovery || snapshot.realtimeConfig?.idleRecovery || null,
        gameSessionId: gameSessionIdRef.current,
      };
      realtimeConfigRef.current = realtimeConfig;

      await webRtcInitializer(
        result.realtimeSessionToken,
        initialConversation,
        result.tools || [],
        sessionLimitsRef.current,
        realtimeConfig
      );

      setConversationStarted(true);
      setActiveSessionRestore({ status: 'resumed', error: '', reason: '' });
      persistActiveSessionSnapshot({
        status: 'active',
        activeTools: result.tools || [],
        realtimeConfig,
      });
      return true;
    } catch (error) {
      console.error('Failed to resume active game session:', error);
      setConversationStarted(false);
      setActiveSessionRestore({
        status: 'failed',
        error: error.message || 'Failed to resume game.',
        reason: '',
      });
      toast.error(error.message || 'Failed to resume game.');
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Save conversation log
  const saveConversationLog = async () => {
    try {
      const currentConversation = conversationRef.current;
      const currentMessageCount = messageCountRef.current;
      const currentSelectedPack = selectedPackRef.current;
      const currentTotalUsage = totalUsageRef.current;

      // console.log("🔍 DEBUG: authenticatedUser object:", authenticatedUser);
      // console.log("🔍 DEBUG: user ID:", authenticatedUser?.user?.id);

      const userId = getUserId();
      // console.log("🔍 DEBUG: resolved user ID:", userId);

      const conversationData = conversationLogger.createConversationData({
        userId: userId,
        questPack: selectedGamePackRequiresQuestPack ? currentSelectedPack : null,
        modelUsed: realtimeModelRef.current,
        reasoningEffort: realtimeReasoningEffortRef.current,
        messageCount: currentMessageCount,
        startedAt: currentConversation[0]?.timestamp || new Date().toISOString(),
        endedAt: new Date().toISOString(),
        maxInputTokens: sessionLimitsRef.current?.max_input_tokens,
        maxOutputTokens: sessionLimitsRef.current?.max_output_tokens,
        warningThreshold: sessionLimitsRef.current?.warning_threshold,
      });

      const usageData = conversationLogger.createUsageData({
        newInputTokens: currentTotalUsage.newInputTokens,
        cachedTokens: currentTotalUsage.cachedTokens,
        outputTokens: currentTotalUsage.outputTokens,
        totalCost: currentTotalUsage.totalCost,
        newInputTextTokens: currentTotalUsage.newInputTextTokens,
        newInputAudioTokens: currentTotalUsage.newInputAudioTokens,
        cachedTextTokens: currentTotalUsage.cachedTextTokens,
        cachedAudioTokens: currentTotalUsage.cachedAudioTokens,
        outputTextTokens: currentTotalUsage.outputTextTokens,
        outputAudioTokens: currentTotalUsage.outputAudioTokens,
        usageEvents: currentTotalUsage.usageEvents
      });

      const metadata = conversationLogger.createMetadata({
        clientInfo: {
          ...clientInfo,
          requestId: sessionRequestIdRef.current,
        }
      });

      // Include linkage to game session if available
      const linkedData = { ...conversationData, game_session_id: gameSessionIdRef.current || null };
      await conversationLogger.logConversation(linkedData, usageData, metadata);
    } catch (error) {
      console.error('Failed to log conversation:', error);
      await reportClientError({
        message: 'Failed to persist conversation log',
        error,
        category: 'conversation_log_failure',
        requestId: sessionRequestIdRef.current,
        gameSessionId: gameSessionIdRef.current,
      });
    }
  };

  // Start conversation flow
  const startConversation = async (initializeWebRTCConnectionOverride) => {
    setLoading(true);
    try {
      setFinalResults(null);

      if (!selectedGamePack) {
        toast.error('Select a Game before starting.');
        throw new Error('Missing game selection.');
      }

      if (selectedGamePackRequiresQuestPack && questPackQuestions.length === 0) {
        toast.error('No Quest Packs available for this Game.');
        throw new Error('Missing quest pack options.');
      }

      if (selectedGamePackRequiresQuestPack && !selectedPack) {
        toast.error('This Game requires a Quest Pack before starting.');
        throw new Error('Missing quest pack selection.');
      }
      const token = localStorage.getItem('auth_token');
      const requestId = generateRequestId();
      sessionRequestIdRef.current = requestId;
      const webRtcInitializer = typeof initializeWebRTCConnectionOverride === 'function'
        ? initializeWebRTCConnectionOverride
        : initializeWebRTCConnection;

      if (typeof webRtcInitializer !== 'function') {
        throw new Error('Missing WebRTC initializer.');
      }

      const response = await fetch('/.netlify/functions/start-paid-game', {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "X-Request-Id": requestId,
        },
        body: JSON.stringify({
          gamePackId: selectedGamePack,
          questPackId: selectedGamePackRequiresQuestPack ? selectedPack : null,
          conversationMode,
          requestId,
        }),
      });

      const { data, text, contentType } = await parseApiResponse(response);

      if (!response.ok || data?.success === false) {
        if (data?.error === 'INSUFFICIENT_CREDITS') {
          setWallet((current) => ({
            ...current,
            creditBalance: data.currentCredits ?? current.creditBalance,
          }));
          toast.error(`You need ${data.requiredCredits} credits to start this game. You currently have ${data.currentCredits}.`);
          const insufficientCreditsError = new Error('Insufficient credits.');
          insufficientCreditsError.userFacingHandled = true;
          throw insufficientCreditsError;
        }

        if (data?.error === 'CONTENT_NOT_OWNED') {
          toast.error(data.message || 'This content is locked for your account.');
          const entitlementError = new Error(data.message || 'Content not owned.');
          entitlementError.userFacingHandled = true;
          throw entitlementError;
        }

        let fallbackMessage = `Failed to fetch conversation. Status: ${response.status}`;

        if (text) {
          fallbackMessage = contentType.includes('text/html')
            ? `start-paid-game returned ${response.status} with a non-JSON error response. Check Netlify function logs.`
            : text.slice(0, 400);
        }

        const backendMessage = data?.message || data?.error || fallbackMessage;
        toast.error(backendMessage);
        const startError = new Error(backendMessage);
        startError.userFacingHandled = true;
        throw startError;
      }

      if (typeof data.remainingCredits === 'number') {
        setWallet((current) => ({
          ...current,
          creditBalance: data.remainingCredits,
        }));
      }

      if (data.realtimeSessionToken) {
        const result = data.data || data;

        if (result.dynamicPrompt) {
          try {
            const initialConversation = createInitialConversation(result.dynamicPrompt);

            setConversation(initialConversation);
            conversationRef.current = initialConversation;
            setActiveTools(result.tools || []);
            realtimeModelRef.current = result.model_used || 'gpt-realtime-1.5';
            realtimeReasoningEffortRef.current = result.reasoning_effort || 'low';

            usageTrackerRef.current = new UsageTracker(conversationMode);

            // Capture the gameSessionId for later finalization
            try {
              gameSessionIdRef.current = data.gameSessionId || data.game_session_id || null;
              sessionStartedAtRef.current = new Date().toISOString();
            } catch {}

            // Snapshot session limits for guardrails & logging
            try {
              const limits = data.sessionLimits || null;
              if (limits && typeof limits === 'object') {
                sessionLimitsRef.current = {
                  max_input_tokens: Number(limits.max_input_tokens) || 10000,
                  max_output_tokens: Number(limits.max_output_tokens) || 10000,
                  warning_threshold: Number(limits.warning_threshold) || 0.8,
                  idle_warning_seconds: Number(limits.idle_warning_seconds) || 120,
                  idle_auto_end_seconds: Number(limits.idle_auto_end_seconds) || 30,
                };
              } else {
                sessionLimitsRef.current = {
                  max_input_tokens: 10000,
                  max_output_tokens: 10000,
                  warning_threshold: 0.8,
                  idle_warning_seconds: 120,
                  idle_auto_end_seconds: 30,
                };
              }
              setSessionLimits(sessionLimitsRef.current);
            } catch {
              sessionLimitsRef.current = {
                max_input_tokens: 10000,
                max_output_tokens: 10000,
                warning_threshold: 0.8,
                idle_warning_seconds: 120,
                idle_auto_end_seconds: 30,
              };
              setSessionLimits(sessionLimitsRef.current);
            }

            const realtimeConfig = {
              model: result.model_used || 'gpt-realtime-1.5',
              reasoningEffort: result.reasoning_effort || 'low',
              outputModalities: result.output_modalities || null,
              voice: result.voice || null,
              idleRecovery: result.idle_recovery || null,
              gameSessionId: gameSessionIdRef.current,
            };
            realtimeConfigRef.current = realtimeConfig;
            persistActiveSessionSnapshot({ status: 'starting' });

            await webRtcInitializer(
              result.realtimeSessionToken,
              initialConversation,
              result.tools || [],
              sessionLimitsRef.current,
              realtimeConfig
            );
            rememberRecentlyPlayedContent();
            persistActiveSessionSnapshot({ status: 'active' });
            setConversationStarted(true);
          } catch (error) {
            console.error("Failed to initialize WebRTC connection:", error);
            await reportClientError({
              message: 'Failed to initialize WebRTC connection',
              error,
              category: 'webrtc_init_failure',
              requestId: sessionRequestIdRef.current,
              gameSessionId: gameSessionIdRef.current,
            });
            toast.error("Failed to start Cringe Quest. Please try again.");
          }
        } else {
          toast.error("Missing dynamic prompt from the server.");
          throw new Error("No dynamic prompt provided by startConversation.");
        }
      } else {
        toast.error("Unable to start the session - missing token.");
        throw new Error('No realtimeSessionToken found in response.');
      }
    } catch (error) {
      setActiveTools([]);
      if (!error.userFacingHandled) {
        toast.error(error.message || "Error starting conversation.");
      }
      console.error('Error starting conversation:', error);
      await reportClientError({
        message: error.message || 'Error starting conversation',
        error,
        category: 'start_conversation_failure',
        requestId: sessionRequestIdRef.current,
        gameSessionId: gameSessionIdRef.current,
        metadata: {
          selectedGamePack,
          selectedPack: selectedGamePackRequiresQuestPack ? selectedPack : null,
          conversationStarted: conversationRef.current.length > 0,
        },
      });

      // Log the error if we have conversation data
      if (conversationRef.current.length > 0) {
        try {
          const currentConversation = conversationRef.current;
          const currentMessageCount = messageCountRef.current;
          const currentSelectedPack = selectedPackRef.current;
          const currentTotalUsage = totalUsageRef.current;

          const conversationData = conversationLogger.createConversationData({
            userId: getUserId(),
            questPack: selectedGamePackRequiresQuestPack ? currentSelectedPack : null,
            modelUsed: realtimeModelRef.current,
            reasoningEffort: realtimeReasoningEffortRef.current,
            messageCount: currentMessageCount,
            startedAt: currentConversation[0]?.timestamp || new Date().toISOString(),
            endedAt: new Date().toISOString()
          });

          const usageData = conversationLogger.createUsageData({
            newInputTokens: currentTotalUsage.newInputTokens,
            cachedTokens: currentTotalUsage.cachedTokens,
            outputTokens: currentTotalUsage.outputTokens,
            totalCost: currentTotalUsage.totalCost,
            newInputTextTokens: currentTotalUsage.newInputTextTokens,
            newInputAudioTokens: currentTotalUsage.newInputAudioTokens,
            cachedTextTokens: currentTotalUsage.cachedTextTokens,
            cachedAudioTokens: currentTotalUsage.cachedAudioTokens,
            outputTextTokens: currentTotalUsage.outputTextTokens,
            outputAudioTokens: currentTotalUsage.outputAudioTokens,
            usageEvents: currentTotalUsage.usageEvents
          });

          const metadata = conversationLogger.createMetadata({
            clientInfo: {
              ...clientInfo,
              requestId: sessionRequestIdRef.current,
            }
          });

          const linkedErrorData = { ...conversationData, game_session_id: gameSessionIdRef.current || null };
          await conversationLogger.logError(linkedErrorData, usageData, metadata, error.message);
        } catch (logError) {
          console.error('Failed to log conversation error:', logError);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  // End conversation flow
  const endConversation = async (options = {}) => {
    const {
      skipFinalize = false,
      endedReason = 'completed',
      aiReplyCount = null,
    } = options || {};

    try {
      const latestPlayers = playersRef.current;
      const latestTotalUsage = totalUsageRef.current;

      setFinalResults({
        players: { ...latestPlayers },
        totalCost: latestTotalUsage.totalCost || 0,
      });

      await saveConversationLog();

      // Reset usage tracker
      if (usageTrackerRef.current) {
        if (typeof usageTrackerRef.current.reset === 'function') {
          usageTrackerRef.current.reset();
        }
        usageTrackerRef.current = null;
      }

      setConversation([]);
      setMessageCount(1);
      setConversationStarted(false);
      setCurrentQuestion({ question: '', round: null }); // Clear the current question
      setPlayers({}); // Clear the scores at the end of each game
      setActiveTools([]);
      clearActiveGameSessionSnapshot();

      // Reset usage state
      setTotalUsage(createEmptyUsageTotals());

      console.log("Conversation ended.");

      // Attempt to finalize billing for the session (best-effort)
      try {
        if (skipFinalize) {
          return;
        }

        let token = null;
        try {
          token = localStorage.getItem('auth_token');
        } catch {
          token = null;
        }

        const payload = {
          gameSessionId: gameSessionIdRef.current,
          endedReason,
          messageCount: messageCountRef.current || 0,
          ...(aiReplyCount !== null && aiReplyCount !== undefined && Number.isFinite(Number(aiReplyCount))
            ? { aiReplyCount: Number(aiReplyCount) }
            : {}),
          requestId: sessionRequestIdRef.current,
          token,
        };
        const body = JSON.stringify(payload);
        const beacon = typeof navigator !== 'undefined' ? navigator.sendBeacon : null;
        const sentByBeacon = typeof beacon === 'function'
          ? beacon.call(navigator, '/.netlify/functions/end-paid-game', body)
          : false;

        if (!sentByBeacon) {
          fetch('/.netlify/functions/end-paid-game', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body,
            keepalive: true,
          }).catch(() => {});
        }
      } catch (e) {
        console.warn('End conversation finalization failed:', e);
        await reportClientError({
          message: 'End conversation finalization failed',
          error: e,
          category: 'finalize_game_session_failure',
          requestId: sessionRequestIdRef.current,
          gameSessionId: gameSessionIdRef.current,
        });
      }
    } catch (error) {
      console.error("Error ending conversation:", error);
      await reportClientError({
        message: 'Error ending conversation',
        error,
        category: 'end_conversation_failure',
        requestId: sessionRequestIdRef.current,
        gameSessionId: gameSessionIdRef.current,
      });
    }
  };

  // Public helper allowing callers to finalize with an explicit aiReplyCount
  const finalizeGameSession = async (endedReason = 'completed', aiReplyCount = 0) => {
    try {
      if (!gameSessionIdRef.current) return;
      const token = localStorage.getItem('auth_token');
      const payload = {
        gameSessionId: gameSessionIdRef.current,
        endedReason,
        messageCount: messageCountRef.current || 0,
        aiReplyCount: Number(aiReplyCount) || 0,
        requestId: sessionRequestIdRef.current,
        token,
      };
      const res = await fetch('/.netlify/functions/end-paid-game', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(payload),
        keepalive: true,
      });
      let data = null;
      try {
        data = await res.json();
      } catch {}

      if (res.ok && data && data.refunded && typeof data.refunded_credits === 'number' && data.refunded_credits > 0) {
        toast.success(`Refund applied: ${data.refunded_credits} credits returned to your wallet.`);
        // Optimistically update wallet balance so UI reflects the refund without a reload
        setWallet((current) => ({
          ...current,
          creditBalance: (current?.creditBalance || 0) + data.refunded_credits,
        }));
      }
    } catch (e) {
      // Non-fatal: finalization is best-effort for UX, RPC is idempotent
      console.warn('Finalize session failed:', e);
      await reportClientError({
        message: 'Finalize session failed',
        error: e,
        category: 'finalize_game_session_failure',
        requestId: sessionRequestIdRef.current,
        gameSessionId: gameSessionIdRef.current,
      });
    }
  };

  const closeFinalResults = () => {
    setFinalResults(null);
  };

  // Update player scores
  const updateScores = (scores) => {
    if (Array.isArray(scores)) {
      const newPlayers = {};
      scores.forEach(({ player, rounds, total }) => {
        newPlayers[player] = { rounds, total };
      });
      setPlayers(newPlayers);
      // console.log("🎯 Player scores updated:", JSON.stringify(newPlayers, null, 2));
    }
  };

  // Display current question
  const displayQuestion = (question, round) => {
    if (question && typeof round === 'number') {
      setCurrentQuestion({ question, round });
      // console.log(`📝 Displaying question for round ${round}:`, question);
    }
  };

  // Handle usage updates
  const handleUsageUpdate = (usageData) => {
    setTotalUsage(usageData);
    if (onUsageUpdate) {
      onUsageUpdate(usageData);
    }
    // console.log("💰 Updated usage totals:", usageData);
  };

  return {
    // State
    conversationStarted,
    loading,
    activeSessionRestore,
    players,
    conversation,
    conversationRef,
    messageCount,
    gamePacks: enrichedGamePacks,
    selectedGamePack,
    selectedGamePackDetails,
    selectedPack,
    creditBalance: wallet.creditBalance,
    wallet,
    walletLoaded,
    walletError,
    purchasingItemId,
    questPackQuestions,
    questPackLoading,
    questPackError,
    readyToStart,
    selectedGamePackCost,
    projectedRemainingCredits,
    currentQuestion,
    totalUsage,
    activeTools,
    finalResults,
    selectedGamePackRequiresQuestPack,
    sessionLimits,
    
    // Actions
    startConversation,
    hydrateActiveSessionSnapshot,
    resumeActiveSessionFromSnapshot,
    endConversation,
    setSelectedGamePack,
    clearSelectedGamePack,
    setSelectedPack,
    updateScores,
    displayQuestion,
    setConversation,
    appendConversationMessage,
    handleRealtimeConversationEvent,
    setMessageCount,
    handleUsageUpdate,
    closeFinalResults,
    fetchWallet,
    purchaseCredits,
    purchaseStoreItem,
    finalizeGameSession,
    persistActiveSessionSnapshot,
    sendActiveGameSessionHeartbeat,
    
    // Utilities
    getUserId,
    saveConversationLog
  };
};

export default useGameState;
